import { useMemo } from 'react';
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import {
  multipartUploadAbort,
  multipartUploadComplete,
  multipartUploadParts,
  multipartUploadPartsUploadParams
} from '@api/MultipartUpload';
import { apiClient } from '@api/client';
import { nonNull } from '@helpers/non-null';

function createUppy(campaignId: string) {
  const uppy = new Uppy({
    autoProceed: true
  });
  uppy.use(AwsS3, {
    shouldUseMultipart: true,
    getChunkSize() {
      const MB = 1024 * 1024;
      return 20 * MB;
    },
    async createMultipartUpload(file) {
      const {
        data: { attachmentId, uploadId, key }
      } = await apiClient.attachment.attachmentControllerInitAttachmentUpload({
        targetId: campaignId,
        type: 'campaign-attachment',
        fileName: nonNull(file.name),
        fileSize: nonNull(file.size)
      });
      file.meta.attachmentId = attachmentId;
      uppy.setFileMeta(file.id, { attachmentId });
      return { uploadId, key };
    },
    async listParts(file, { key, uploadId }) {
      const {
        data: { parts }
      } = await multipartUploadParts({ key, uploadId: nonNull(uploadId) });
      return parts;
    },
    async signPart(file, { key, uploadId, partNumber }) {
      try {
        const {
          data: { presignedUrls, headers }
        } = await multipartUploadPartsUploadParams({
          key,
          uploadId,
          partNumber: [partNumber]
        });
        return { url: presignedUrls[partNumber], headers };
      } catch (err) {
        console.warn(err, '-> prepareUploadParts');
        throw err;
      }
    },
    async abortMultipartUpload(file, { key, uploadId }) {
      await multipartUploadAbort({ key, uploadId: nonNull(uploadId) });
    },
    async completeMultipartUpload(file, { key, uploadId, parts }) {
      const { data } = await multipartUploadComplete({
        key,
        uploadId,
        parts: parts.map((part) => ({
          PartNumber: nonNull(part.PartNumber),
          ETag: nonNull(part.ETag)
        }))
      });
      return data;
    }
  });
  return uppy;
}

export function useUppy(campaignId: string) {
  return useMemo(() => createUppy(campaignId), [campaignId]);
}
